// src/utils/notificationDisplay.ts
//
// The SINGLE display vocabulary for the in-app notification bell (Story 4.9).
// One typed map from YakNotificationType to a bell title, one target resolver,
// one count-label formatter. Components import these — no bell copy is restated
// anywhere else (the ticketDisplay.questionTypeLabel discipline applied here).
//
// The map is a Record<YakNotificationType, …> keyed by the AS-BUILT type union
// (3.5's vocabulary + 4.7's auto_close_notice + 6.3's reference_codes). Adding a
// new type value without a bell disposition FAILS typecheck/CI — the 4.2 parity-test
// discipline. Rep-facing (email/sms) types map to `null` (never rendered — rep+in_app
// is CHECK-impossible per 3.5, so
// these never reach the bell, but the panel filters them defensively). An unknown/
// future string falls back to a generic entry (fail toward rendering — the
// project's fail-safe-parsing direction; the typed Record means "unknown" can only
// mean data ahead of deployed code, which should be seen, not swallowed).

import type {
  YakNotificationType,
  YakNotificationWithTicket,
} from '../types/yak'

export interface BellEntry {
  title: string
}

// Authored copy — calm, counts-and-verbs; no route labels, no alarm words, no red
// (the brand-600 dot/count is identity, not danger — DESIGN.md's red rule).
const BELL_ENTRIES: Record<YakNotificationType, BellEntry | null> = {
  assignment: { title: 'New ticket assigned to you' },
  informational_assignment: {
    title: 'Answered on the call — assigned to you for the record',
  },
  response: { title: 'A ticket you handled was answered' },
  rep_reply: { title: 'The rep replied' },
  nudge: { title: 'Still open — needs a response' },
  dead_letter: { title: 'An inbound email needs review' },
  bounce: { title: 'An email bounced' },
  // Email-only (never minted as in_app): mapped null, never rendered.
  recap: null,
  ticket_anchor: null,
  auto_close_notice: null,
  // SMS-only (Story 6.3): rep-facing, never minted as in_app — mapped null.
  reference_codes: null,
  // SMS-only (Story 6.2): the 2FA one-time code. Rep-facing and mid-call — a
  // console user has nothing to do about it, so it never renders in the bell. A
  // FAILED otp row still surfaces in Settings -> Failures like any other outbox
  // failure; that is the surface an admin can act on.
  otp: null,
}

// The generic fallback for an unknown/future type string (fail toward visible).
const FALLBACK_ENTRY: BellEntry = { title: 'Notification' }

// The bell disposition for a known type: an entry, or null for email-only types.
export function bellEntry(type: YakNotificationType): BellEntry | null {
  return BELL_ENTRIES[type]
}

// Runtime-safe title resolver: a known non-null entry, or the generic fallback
// for an unknown string (data ahead of code). Email-only types are filtered by
// the panel BEFORE this is reached, so an explicit null maps to the fallback here
// rather than being rendered blank.
export function bellEntryOrFallback(type: string): BellEntry {
  const entry = BELL_ENTRIES[type as YakNotificationType]
  return entry ?? FALLBACK_ENTRY
}

// Whether a row should render in the bell at all. The rep-facing set
// (recap/ticket_anchor/auto_close_notice/reference_codes, mapped null) is filtered out
// defensively — rep+in_app is CHECK-impossible, so these never actually reach the
// bell, but the panel never renders a blank row for them. An unknown/future type
// (undefined in the map) IS renderable (fail toward visible, via the fallback).
export function isBellRenderable(type: string): boolean {
  return BELL_ENTRIES[type as YakNotificationType] !== null
}

// Where clicking the item navigates. dead_letter/bounce → the failure remediation
// surface (4.8), consistent with 4.8's own email deep links, even when ticket_id
// is set. Otherwise a ticket-linked row → the ticket detail route (4.1/3.7's
// deep-link target). A row with neither → null (mark-read only, no navigation).
export function bellTarget(
  notification: Pick<YakNotificationWithTicket, 'type' | 'ticket_id'>
): string | null {
  if (notification.type === 'dead_letter' || notification.type === 'bounce') {
    return '/settings/failures'
  }
  if (notification.ticket_id) {
    return `/tickets/${notification.ticket_id}`
  }
  return null
}

// The unread-count cap: the badge and its label never show a bare, unbounded
// number; anything over 99 reads "99+".
export const UNREAD_COUNT_CAP = 99

export function unreadCountDisplay(n: number): string {
  return n > UNREAD_COUNT_CAP ? `${UNREAD_COUNT_CAP}+` : String(n)
}

// The accessible label — never a bare number (EXPERIENCE.md's accessibility
// floor: counts are labeled, e.g. "4 unread notifications").
export function unreadCountLabel(n: number): string {
  return `${unreadCountDisplay(n)} unread notifications`
}
